import { Component } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { ConfigService } from '../app/services/config.service';
import { IdleService } from '../app/idle/services/idle.service';
import { UserService } from '../app/services/user.service';
import { ThemeBuilderService } from '../app/theme/services/theme-builder.service';
import { Subscription, filter, map, mergeMap } from 'rxjs';

@Component({
	selector: 'kv-app-root',
	templateUrl: './app.component.html',
	standalone: false,
	styleUrl: './app.component.css'
})
export class AppComponent {

	private configSubscription: Subscription = new Subscription();
	private userServiceSubscription: Subscription = new Subscription();

	defaultPageTitle: string = 'Welcome to Knowvation';
	pageTitles: any = {};

	constructor(
		private configService: ConfigService,
		private userService: UserService,
		private idleService: IdleService,
		private activatedRoute: ActivatedRoute,
		private router: Router,
		private titleService: Title,
		public themeBuilder: ThemeBuilderService
	) {
		// Note: IdleService initializes automatically

		router.initialNavigation(); // TODO do we need this?
	}

	ngOnInit(): void {
		// Initialization notes:
		// It's possible that some service events, such as configs loaded and user localization loaded occur
		// BEFORE the app is fully initialized, and this component's ngOnInit is activated.
		// Watch for 'Angular is running in development mode' in the console to determine when the app.component init occurs.

		this.setTitleOnNavigation();
		this.themeBuilder.applyThemes();

		
		// FIXME: There must be a better way to to run this additional initialization logic than depending on these events.
		
		// This event is usually issued twice on fresh startup:
		// The first event does not contain the full config from the server, only the default app json config from assets.
		// The second event runs after the app is fully initialized, and is fully populated.
		// But on browser refresh, this is not guaranteed to be emitted *after* app initialization.
		this.configSubscription = this.configService.configsLoadedEvent.subscribe(() => {
			this.initializeAppTitles();

			let theme = this.userService?.user?.theme || this.userService.getLocalStorageData().theme || "";
			this.setTheme(theme);
		});

		// On fresh startup, this event is not guaranteed to be emitted before this component initializes and could be missed,
		// which is why the logic is duplicated in the config service subscription.
		this.userServiceSubscription = this.userService.broadcastEvent.subscribe((eventId: string) => {
			switch (eventId) {
				case 'localizationLoaded':
					this.initializeAppTitles();
					
					let theme = this.userService?.user?.theme || this.userService.getLocalStorageData().theme || "";
					this.setTheme(theme);
					break;
			}
		});
	}

	ngOnDestroy() {
		if (this.configSubscription) {
			this.configSubscription.unsubscribe();
		}
		if (this.userServiceSubscription) {
			this.userServiceSubscription.unsubscribe();
		}
	}

	private initializeAppTitles() {
		this.pageTitles = this.configService.getConfig()?.ui?.pageTitle || {};
		this.setInitialTitle();
	}

	private setInitialTitle() {
		let route = this.activatedRoute;
		while (route.firstChild) {
			route = route.firstChild;
		}
		const stateId = route.snapshot.data?.['stateId'];
		const fallBackTitle = route.snapshot.data?.['pageTitle'] ?? this.defaultPageTitle;
		let title = this.pageTitles[stateId] || fallBackTitle;
		this.titleService.setTitle(title);
	}

	private setTitleOnNavigation() {
		this.router.events.pipe(
			filter((event: any) => event instanceof NavigationEnd),
			map(() => {
				let route = this.activatedRoute;
				while (route.firstChild) {
					route = route.firstChild;
				}
				return route;
			}),
			mergeMap((route: any) => route.data),
		).subscribe((data: any) => {
			const stateId = data?.['stateId'];
			const fallBackTitle = data?.['pageTitle'] ?? this.defaultPageTitle;
			let title = this.pageTitles[stateId] || fallBackTitle;
			this.titleService.setTitle(title);
		});
	}

	private setTheme(theme: any): void {
		this.themeBuilder.setThemeAttribute(theme);
		console.log('Loaded user theme: %s', theme);

		const tmp = this.userService.getLocalStorageData();
		tmp.theme = theme;
		this.userService.setLocalStorageData(tmp);
		this.userService.updatePartialUser({ theme });
	}
}
